import { Mesh, Vector3, VertexBuffer } from '@babylonjs/core';

/** Helpers for door slab geometry, materials, UVs, and CSG normal correction. */

/** Name of the baked siding mesh shipped in older door models. */
export const DOOR_SIDING_OBJ = '_siding_obj';

/** Name of the slab mesh shipped in newer door models. */
export const DOOR_SLAB_OBJ = '_slab_obj';

/** Name of the generated mesh rendering the exterior siding face of a door slab. */
export const DOOR_SLAB_OUTSIDE_MESH = 'door_slab_outside';

/** Name of the generated mesh rendering the interior face of a slab based door. */
export const DOOR_SLAB_INSIDE_MESH = 'door_slab_inside';

/** Material used for the exterior face of a door slab. */
export const WOOD_SIDING_MAT = 'wood_siding_mat';

/** Material used for the interior face of a door slab. */
export const SLAB_BACKSIDE_WOOD_MAT = 'slab_backside_wood_mat';

/** Fallback material name used when the baked slab mesh has no material name. */
export const DOOR_SIDING_MAT = 'door_siding_mat';

/** Default texture size used when a siding material is designed. */
export const SLAB_UNIT_SIZE = 8;

/**
 * Door slab mesh data registered for a later UV scale refresh.
 */
export interface TDoorSlabMeshData {
	uniqueId: number;
	height: number;
	width: number;
}

/**
 * Data returned by `resolveDoorSlabMesh`.
 */
export interface TDoorSlabMesh {
	mesh: any;
	isNewDoor: boolean;
}

/**
 * Options of `projectPlanarUVs`.
 */
export interface TPlanarUVOptions {
	/** Flips the U axis (left/right) of the projected UVs. */
	mirrorU?: boolean;
	/** Flips the V axis (top/bottom) of the projected UVs. */
	mirrorV?: boolean;
}

/**
 * Rounds a number to 3 decimals, mirroring `helpers.toFixed3`.
 *
 * @param {number} value - Value to round
 * @returns {number} The rounded value
 */
const round3 = (value: number) => Number(Number(value).toFixed(3));

/**
 * Finds the baked slab mesh of a door component.
 *
 * Older models ship a `*_siding_obj` mesh, newer ones a `*_slab_obj` mesh. The latter is flagged as
 * a new door because it also needs a generated interior mesh.
 *
 * @param {Array} childMeshes - Child meshes of the door root mesh
 * @returns {TDoorSlabMesh|null} `{ mesh, isNewDoor }`, or `null` when no usable slab mesh exists
 */
export const resolveDoorSlabMesh = (childMeshes: any[] = []): TDoorSlabMesh => {
	const meshes = Array.isArray(childMeshes) ? childMeshes : [];
	const firstByName = (value: string) =>
		meshes.find((mesh) => typeof mesh?.name === 'string' && mesh.name.includes(value));

	let mesh = firstByName(DOOR_SIDING_OBJ);
	let isNewDoor = false;
	if (!mesh) {
		mesh = firstByName(DOOR_SLAB_OBJ);
		isNewDoor = true;
	}

	if (!mesh || typeof mesh.getTotalVertices !== 'function' || mesh.getTotalVertices() === 0) {
		return null;
	}

	const positions = mesh.getVerticesData('position');
	if (!positions || positions.length === 0) {
		return null;
	}

	return { mesh, isNewDoor };
};

/**
 * Projects vertex positions to planar UVs on the X/Y plane, normalized to 0..1.
 *
 * The projection is computed from local space positions, so it is independent of the mesh
 * position / rotation / scaling. A flipped axis is written into the UVs themselves, which keeps
 * the mirror intact through any later material UV scale / offset change (`coord = uv * scale +
 * offset`) and applies to every texture channel sharing the UV set.
 *
 * @param {Array} positions - Flat vertex positions (x, y, z per vertex)
 * @param {Object} [options] - Projection options
 * @param {boolean} [options.mirrorU] - Flips the U axis (left/right)
 * @param {boolean} [options.mirrorV] - Flips the V axis (top/bottom)
 * @returns {Float32Array|null} UVs (u, v per vertex), or `null` when there is nothing to project
 */
export const projectPlanarUVs = (
	positions: ArrayLike<number>,
	options: TPlanarUVOptions = {},
): Float32Array => {
	if (!positions || positions.length === 0) {
		return null;
	}

	const mirrorU = true === options?.mirrorU;
	const mirrorV = true === options?.mirrorV;

	const uvs = new Float32Array((positions.length / 3) * 2);
	const vCount = positions.length / 3;
	let minX = Infinity,
		maxX = -Infinity;
	let minY = Infinity,
		maxY = -Infinity;

	for (let i = 0; i < vCount; i++) {
		const x = positions[i * 3];
		const y = positions[i * 3 + 1];
		if (x < minX) minX = x;
		if (x > maxX) maxX = x;
		if (y < minY) minY = y;
		if (y > maxY) maxY = y;
	}

	const dx = maxX - minX || 1;
	const dy = maxY - minY || 1;
	for (let i = 0; i < vCount; i++) {
		const u = (positions[i * 3] - minX) / dx;
		const v = (positions[i * 3 + 1] - minY) / dy;

		uvs[i * 2] = mirrorU ? 1 - u : u;
		uvs[i * 2 + 1] = mirrorV ? 1 - v : v;
	}

	return uvs;
};

/**
 * Resolves the materials used to render a door slab.
 *
 * A multi-material (baked door) provides dedicated exterior / interior sub materials, otherwise the
 * mesh material itself is reused for both faces.
 *
 * Any material exposing a `subMaterials` array is treated as a multi-material, miming how
 * Babylon.js `MultiMaterial` is detected elsewhere in the plugin.
 *
 * @param {Object} material - Material of the baked slab mesh
 * @returns {Object} `{ outsideMatName, outsideMat, insideMat }`
 */
export const resolveDoorSlabMaterials = (material: any) => {
	let insideMat = material;
	let outsideMatName = material?.name || DOOR_SIDING_MAT;
	let outsideMat = material;

	if (material && Array.isArray(material.subMaterials)) {
		outsideMatName = WOOD_SIDING_MAT;
		outsideMat = material.subMaterials.find(
			(subMat) => subMat && subMat.name.toLowerCase().includes(WOOD_SIDING_MAT),
		);
		insideMat = material.subMaterials.find(
			(subMat) => subMat && subMat.name.toLowerCase().includes(SLAB_BACKSIDE_WOOD_MAT),
		);
	}

	return { outsideMatName, outsideMat, insideMat };
};

/**
 * Computes the siding UV scale of a door slab from its dimensions.
 *
 * @param {Object} params - Parameters
 * @param {number} params.width - Slab width
 * @param {number} params.height - Slab height
 * @param {number} params.initUScale - Initial U scale coming from the network data
 * @param {number} params.initVScale - Initial V scale coming from the network data
 * @param {number} [params.unitSize] - Designed texture size, 8 by default
 * @returns {Object} `{ uScale, vScale }` rounded to 3 decimals
 */
export const getDoorSlabUVScale = (params: {
	width: number;
	height: number;
	initUScale: number;
	initVScale: number;
	unitSize?: number;
}) => {
	const { width, height, initUScale, initVScale, unitSize = SLAB_UNIT_SIZE } = params;

	return {
		vScale: round3(height / (unitSize / initVScale)),
		uScale: round3(width / (unitSize / initUScale)),
	};
};

/**
 * Checks whether a door slab mesh is already registered.
 *
 * @param {TDoorSlabMeshData[]} list - Registered door slab mesh data
 * @param {string} uniqueId - Mesh unique id to check
 * @returns {boolean} `true` when the mesh is already registered
 */
export const hasDoorSlabMeshData = (list: TDoorSlabMeshData[], uniqueId: number): boolean => {
	return Array.isArray(list) && list.some((item) => item.uniqueId === uniqueId);
};

/**
 * Registers a door slab mesh entry if it has not been registered yet.
 *
 * @param {TDoorSlabMeshData[]} list - Registered door slab mesh data
 * @param {TDoorSlabMeshData} entry - Entry to register
 * @returns {boolean} `true` when the entry was added
 */
export const upsertDoorSlabMeshData = (
	list: TDoorSlabMeshData[],
	entry: TDoorSlabMeshData,
): boolean => {
	if (!Array.isArray(list) || !entry || hasDoorSlabMeshData(list, entry.uniqueId)) {
		return false;
	}

	list.push({
		uniqueId: entry.uniqueId,
		height: entry.height,
		width: entry.width,
	});

	return true;
};


/** Restore local normals after legacy CSG writes world-space normals into its output mesh. */
export const restoreDoorSlabCutNormals = (cutMesh: Mesh, sourceMesh: Mesh): void => {
	const normals = cutMesh.getVerticesData(VertexBuffer.NormalKind);
	if (!normals) return;

	const inverseWorld = sourceMesh.computeWorldMatrix(true).clone().invert();
	const normal = Vector3.Zero();
	for (let i = 0; i < normals.length; i += 3) {
		Vector3.TransformNormalToRef(Vector3.FromArray(normals, i), inverseWorld, normal);
		normal.normalize();
		normals[i] = normal.x;
		normals[i + 1] = normal.y;
		normals[i + 2] = normal.z;
	}
	cutMesh.setVerticesData(VertexBuffer.NormalKind, normals);
};
